import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Banknote,
  Camera,
  IdCard,
  Package,
  PackageCheck,
  MapPin,
  RefreshCw,
  Save,
  ShoppingBag,
  User,
} from "lucide-react";
import { getDeliveryConfig, updateDeliveryConfig } from "../../api/settings";
import { showToast } from "../../utils/showToast";
import { useCountry } from "../../context/CountryContext";
import { identityDocumentLabel } from "../../utils/identityDocumentLabel";
import { SectionCard } from "../ui/SectionCard";
import { Switch } from "../ui/Switch";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";

// ─────────────────────────────────────────────────────────────────────────────
// DeliveryFormConfig — Qué datos debe registrar el repartidor al confirmar una
// ENTREGA exitosa, según el tipo de paquete: nombre de quien recibe y/o su
// documento de identidad.
//
// La foto de evidencia NO es configurable: se pide siempre, en las tres
// categorías. Aparece en la pantalla como una fila bloqueada para que el
// operador no se pregunte por qué no está. La única excepción se decide en la
// pestaña de pago contra entrega (cobros por transferencia con solo el
// comprobante); acá solo se avisa que existe.
//
// Las tres categorías las clasifica la app con señales que ya existían:
// `cashOnDelivery.amount > 0` ⇒ pago contra entrega, `type === 'MERCADOLIBRE'`
// ⇒ MercadoLibre, y el resto cae en "otras".
//
// Autocontenido, igual que PickupFormConfig: carga y guarda contra
// GET/PATCH /configuration/delivery, que resuelve el operador dueño desde el JWT
// y mergea POR SECCIÓN (guardar acá no toca las otras configuraciones).
// ─────────────────────────────────────────────────────────────────────────────

// A diferencia de los retiros, acá NO hay mínimo: dejar las dos casillas apagadas
// es una configuración legítima ("solo la foto"), y es justo el caso de pago
// contra entrega que motivó esta pantalla.

/** `other` va primero: es el catch-all y hace de base cuando se comparte la config. */
const CATEGORIES = [
  {
    key: "other",
    icon: Package,
    title: "Otras entregas",
    subtitle: "Todo lo que no sea MercadoLibre ni pago contra entrega.",
  },
  {
    key: "mercadolibre",
    icon: ShoppingBag,
    title: "MercadoLibre",
    subtitle: "Paquetes que llegaron desde MercadoLibre.",
  },
  {
    key: "cashOnDelivery",
    icon: Banknote,
    title: "Pago contra entrega",
    subtitle: "Paquetes en los que el repartidor cobra al entregar.",
  },
];

/** Orden de presentación: nombre primero, documento después. */
const FIELDS = [
  { key: "fullName", icon: User, label: "Nombre y apellido de quien recibe" },
  {
    key: "document",
    icon: IdCard,
    // El label real depende del país del operador (RUT/DNI/Cédula) y se resuelve al render.
    label: null,
  },
];

const DEFAULT_FIELDS = ["fullName", "document"];

// Deben coincidir con delivery-config.schema.ts del BFF. Si divergen, el operador
// puede guardar un radio que el DTO rechaza con 400 sin explicar por que.
const DEFAULT_DISTANCE_RADIUS_M = 150;
const MIN_DISTANCE_RADIUS_M = 20;
const MAX_DISTANCE_RADIUS_M = 5000;

/**
 * Estado editable desde la respuesta del backend (o los defaults si no configuró nada).
 *
 * OJO: se distingue lista AUSENTE (⇒ default) de lista VACÍA (⇒ no pedir nada) con
 * `Array.isArray`, no con `?.length` como hace PickupFormConfig. Acá `[]` es una
 * configuración real y colapsarla al default borraría lo que el operador guardó.
 */
function stateFromConfig(config) {
  const deliveryForm = config?.deliveryForm ?? null;
  const distance = config?.deliveryDistanceCheck ?? null;
  const pick = (list) => (Array.isArray(list) ? [...list] : [...DEFAULT_FIELDS]);
  const radius = Number(distance?.radiusMeters);
  return {
    // `shared` por defecto true: es lo que espera un operador que nunca separó los tipos.
    shared: deliveryForm ? deliveryForm.shared !== false : true,
    other: pick(deliveryForm?.other),
    mercadolibre: pick(deliveryForm?.mercadolibre),
    cashOnDelivery: pick(deliveryForm?.cashOnDelivery),
    // Fail-open, igual que el resolver del BFF: sin sección guardada la
    // verificación aparece APAGADA, nunca encendida por accidente.
    distanceEnabled: distance?.enabled === true,
    distanceRadius: Number.isFinite(radius) && radius > 0 ? radius : DEFAULT_DISTANCE_RADIUS_M,
  };
}

export default function DeliveryFormConfig() {
  const { t } = useTranslation();
  const { countryCode } = useCountry();
  const docLabel = identityDocumentLabel(countryCode);

  const [state, setState] = useState(() => stateFromConfig(null));
  const [snapshot, setSnapshot] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getDeliveryConfig()
      .then((res) => {
        if (!active) return;
        const next = stateFromConfig(res?.data ?? res);
        setState(next);
        setSnapshot(JSON.stringify(next));
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
        } else {
          // No se renderiza el formulario con defaults: con snapshot vacío, Guardar
          // quedaría habilitado y PATCHearía encima de la config real del operador.
          setLoadError(true);
          showToast(t("No se pudo cargar la configuración de entregas"), false);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // `t` fuera de las deps a propósito: su identidad cambia con el idioma y eso
    // refetchearía la config, descartando ediciones sin aviso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const dirty = useMemo(() => JSON.stringify(state) !== snapshot, [state, snapshot]);

  /**
   * El input deja escribir cualquier cosa mientras se edita (borrarlo entero, por
   * ejemplo); el saneo va al guardar, no en cada tecla, para no pelearle al cursor.
   * Sin esto un campo vacío se enviaría como NaN y el DTO respondería 400.
   */
  const clampRadius = (value) => {
    const n = Math.round(Number(value));
    if (!Number.isFinite(n)) return DEFAULT_DISTANCE_RADIUS_M;
    return Math.min(MAX_DISTANCE_RADIUS_M, Math.max(MIN_DISTANCE_RADIUS_M, n));
  };

  const radiusOutOfRange =
    state.distanceEnabled &&
    (!Number.isFinite(Number(state.distanceRadius)) ||
      Number(state.distanceRadius) < MIN_DISTANCE_RADIUS_M ||
      Number(state.distanceRadius) > MAX_DISTANCE_RADIUS_M);

  const toggleField = (categoryKey, fieldKey) =>
    setState((prev) => {
      const current = prev[categoryKey] ?? [];
      const next = current.includes(fieldKey)
        ? current.filter((f) => f !== fieldKey)
        : // Se agrega respetando el orden de FIELDS, no el orden de click: así el
          // arreglo guardado se lee igual que la pantalla.
          FIELDS.map((f) => f.key).filter((k) => k === fieldKey || current.includes(k));
      return { ...prev, [categoryKey]: next };
    });

  // Las categorías que el operador está editando de verdad. Con `shared`, las tres
  // usan la lista de `other` y los otros dos bloques no se muestran.
  const visibleCategories = state.shared ? CATEGORIES.slice(0, 1) : CATEGORIES;

  const handleSave = async () => {
    setSaving(true);
    try {
      // Las tres listas se mandan SIEMPRE, incluso compartiendo: así al separar los
      // tipos el operador recupera lo que tenía en vez de empezar de cero. El backend
      // aplica el `shared` al resolver lo que ve la app.
      // Las dos secciones van en el MISMO PATCH. El backend mergea por sección, así
      // que mandarlas juntas no pisa `pickupForm` ni `remitoRecipients`.
      await updateDeliveryConfig({
        deliveryForm: {
          shared: state.shared,
          other: state.other,
          mercadolibre: state.mercadolibre,
          cashOnDelivery: state.cashOnDelivery,
        },
        deliveryDistanceCheck: {
          enabled: state.distanceEnabled,
          // El radio se guarda aunque la verificación esté apagada, para no perder el
          // valor si el operador la vuelve a encender. Entero: el DTO usa @IsInt.
          radiusMeters: clampRadius(state.distanceRadius),
        },
      });
      setSnapshot(JSON.stringify(state));
      showToast(t("Datos de la entrega guardados"), true);
    } catch {
      showToast(t("No se pudo guardar la configuración"), false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (snapshot) setState(JSON.parse(snapshot));
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-56 w-full" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("Sin configuración disponible")}
        subtitle={t(
          "Tu usuario no pertenece a un operador con configuración de entrega.",
        )}
      />
    );
  }

  if (loadError) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("No se pudo cargar la configuración")}
        subtitle={t("Reintenta en un momento.")}
        action={
          <Button
            variant="outline"
            onClick={() => setReloadKey((k) => k + 1)}
            iconLeft={<RefreshCw size={16} />}
          >
            {t("Reintentar")}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon={<PackageCheck className="w-4 h-4" />}
        title={t("Datos al confirmar una entrega")}
        subtitle={t(
          "Lo que el repartidor debe registrar de quien recibe el paquete. La foto de evidencia se pide siempre.",
        )}
      >
        <Switch
          checked={state.shared}
          onChange={(next) => setState((prev) => ({ ...prev, shared: next }))}
          disabled={saving}
          label={t("Pedir lo mismo en todos los tipos de paquete")}
        />
        <p className="mt-1.5 text-xs text-fg-subtle">
          {state.shared
            ? t("MercadoLibre y pago contra entrega piden lo mismo que el resto.")
            : t("Cada tipo de paquete pide sus propios datos.")}
        </p>
      </SectionCard>

      {visibleCategories.map((category) => {
        const Icon = category.icon;
        const selected = state[category.key] ?? [];
        return (
          <SectionCard
            key={category.key}
            icon={<Icon className="w-4 h-4" />}
            title={t(state.shared ? "Datos a pedir" : category.title)}
            subtitle={t(
              state.shared
                ? "Se piden en todas las entregas, sea cual sea el tipo de paquete."
                : category.subtitle,
            )}
          >
            <div className="flex flex-col gap-3">
              {FIELDS.map((field) => {
                const FieldIcon = field.icon;
                const label = field.label ? t(field.label) : docLabel;
                return (
                  <div key={field.key} className="flex items-center gap-2">
                    <FieldIcon className="w-4 h-4 shrink-0 text-fg-subtle" />
                    <Switch
                      checked={selected.includes(field.key)}
                      onChange={() => toggleField(category.key, field.key)}
                      disabled={saving}
                      label={label}
                    />
                  </div>
                );
              })}
              {/* Fila informativa: la foto no se puede apagar, pero se muestra para
                  que el operador vea la evidencia completa de un vistazo. */}
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 shrink-0 text-fg-subtle" />
                <Switch
                  checked
                  disabled
                  onChange={() => {}}
                  label={t("Foto de evidencia (siempre obligatoria)")}
                />
              </div>
            </div>
            <div aria-live="polite">
              {selected.length === 0 && (
                <p className="mt-3 text-xs text-fg-subtle">
                  {t("Solo se pedirá la foto de evidencia.")}
                </p>
              )}
            </div>
            {/* Con `shared` esta tarjeta también rige para los paquetes con pago contra
                entrega, así que el aviso va en ella o en la de pago contra entrega. */}
            {(state.shared || category.key === "cashOnDelivery") && (
              <p className="mt-3 text-xs text-fg-subtle">
                {t("Excepción: en pago contra entrega por transferencia puedes pedir solo el comprobante del pago, sin estos datos ni la foto. Se configura en la pestaña «Pago contra entrega».")}
              </p>
            )}
          </SectionCard>
        );
      })}

      <SectionCard
        icon={<MapPin className="w-4 h-4" />}
        title={t("Verificación de distancia")}
        subtitle={t(
          "Avisa al repartidor cuando marca una entrega lejos de la dirección del paquete.",
        )}
      >
        <Switch
          checked={state.distanceEnabled}
          onChange={(next) =>
            setState((prev) => ({ ...prev, distanceEnabled: next }))
          }
          disabled={saving}
          label={t("Avisar si el repartidor está lejos del destino")}
        />
        <p className="mt-1.5 text-xs text-fg-subtle">
          {t(
            "Es un aviso, no un bloqueo: el repartidor puede confirmar igual. Así una dirección mal geocodificada no lo deja trabado en la puerta del cliente.",
          )}
        </p>

        {state.distanceEnabled && (
          <div className="mt-4 flex flex-col gap-1.5">
            <label
              htmlFor="delivery-distance-radius"
              className="text-sm font-medium"
            >
              {t("Distancia máxima sin aviso")}
            </label>
            <div className="flex items-center gap-2">
              <input
                id="delivery-distance-radius"
                type="number"
                inputMode="numeric"
                min={MIN_DISTANCE_RADIUS_M}
                max={MAX_DISTANCE_RADIUS_M}
                step={10}
                value={state.distanceRadius}
                disabled={saving}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, distanceRadius: e.target.value }))
                }
                className="w-28 rounded-md border border-border bg-transparent px-2 py-1.5 text-sm"
                aria-describedby="delivery-distance-radius-help"
                aria-invalid={radiusOutOfRange || undefined}
              />
              <span className="text-sm text-fg-subtle">{t("metros")}</span>
            </div>
            <p id="delivery-distance-radius-help" className="text-xs text-fg-subtle">
              {t(
                "Entre {{min}} y {{max}} metros. Por debajo de {{min}} la precisión del GPS vuelve el aviso ruido.",
                { min: MIN_DISTANCE_RADIUS_M, max: MAX_DISTANCE_RADIUS_M },
              )}
            </p>
            <div aria-live="polite">
              {radiusOutOfRange && (
                <p className="text-xs text-danger">
                  {t(
                    "Se guardará ajustado al rango permitido ({{min}}–{{max}} m).",
                    { min: MIN_DISTANCE_RADIUS_M, max: MAX_DISTANCE_RADIUS_M },
                  )}
                </p>
              )}
            </div>
            <p className="mt-1 text-xs text-fg-subtle">
              {t(
                "Los repartidores con una versión antigua de la app no reciben este aviso hasta que actualicen.",
              )}
            </p>
          </div>
        )}
      </SectionCard>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
          {t("Descartar cambios")}
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!dirty || saving}
          iconLeft={!saving ? <Save size={16} /> : undefined}
        >
          {saving ? t("Guardando…") : t("Guardar cambios")}
        </Button>
      </div>
    </div>
  );
}
